// Worker load failures (CODE_REVIEW S13). A Comlink call to a worker whose script never loaded (a 404
// after a deploy, or offline with a stale cache) waits forever. `guard` wraps the remote so every call
// also rejects when the worker reports an error, and calls `onDead` once so the owner can start a fresh
// worker next time.
import type * as Comlink from 'comlink';

export function guard<T>(worker: Worker, api: Comlink.Remote<T>, onDead: () => void): Comlink.Remote<T> {
  let fail: (e: Error) => void = () => {};
  const dead = new Promise<never>((_, reject) => (fail = reject));
  dead.catch(() => {}); // rejected before anyone awaits it: not an unhandled rejection
  worker.addEventListener(
    'error',
    (ev) => {
      ev.preventDefault();
      fail(new Error(`A background worker could not start (${ev.message || 'load error'}); reload the page`));
      onDead();
    },
    { once: true },
  );
  return new Proxy(api as object, {
    get(target, prop) {
      const v = (target as Record<string | symbol, unknown>)[prop];
      if (typeof prop === 'symbol' || prop === 'then') return v;
      return (...args: unknown[]) =>
        Promise.race([(v as (...a: unknown[]) => Promise<unknown>)(...args), dead]);
    },
  }) as Comlink.Remote<T>;
}
